// ─────────────────────────────────────────────────────────────────────────────
// Email Service — calls the Express backend server (Gmail via Nodemailer)
// 
// LOCAL DEV:  http://localhost:5000/api/send-email
// PRODUCTION: https://your-render-app.onrender.com/api/send-email
//
// To switch environments, change REACT_APP_SERVER_URL in your .env file.
// ─────────────────────────────────────────────────────────────────────────────

const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5000';
const SEND_EMAIL_ENDPOINT = `${SERVER_URL}/api/send-email`;

/**
 * Core helper — sends a POST request to the email server
 */
const sendEmail = async (templateType, templateParams) => {
  try {
    console.log('📧 Sending email via server:', SEND_EMAIL_ENDPOINT);
    console.log('Template:', templateType);
    console.log('To:', templateParams.to_email);
    
    const response = await fetch(SEND_EMAIL_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ templateType, templateParams }),
    });

    const result = await response.json();
    console.log('Server response:', result);

    if (response.ok && result.success) {
      console.log(`✅ Email [${templateType}] sent to:`, templateParams.to_email);
      return true;
    } else {
      console.warn(`⚠️ Email [${templateType}] failed:`, result.error);
      return false;
    }
  } catch (error) {
    console.error(`❌ Email [${templateType}] error:`, error.message);
    console.error('Full error:', error);
    return false;
  }
};

// ─── Application (Careers) Confirmation ──────────────────────────────────────
export const sendApplicationConfirmationEmail = async (applicationData) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(applicationData.email)) {
    console.error('❌ Invalid email:', applicationData.email);
    return false;
  }

  return sendEmail('application_confirmation', {
    to_email: applicationData.email,
    to_name: applicationData.fullName,
    applicant_name: applicationData.fullName,
    applicant_email: applicationData.email,
    department: applicationData.selectedRole,
    course_year: applicationData.courseYear,
    contact_number: applicationData.contactNumber,
    motivation: applicationData.motivation,
    skill_level: applicationData.skillLevel,
    application_date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    application_status: 'Received',
    organization_name: 'AIEC IITM Janakpuri',
    website_url: 'https://aiec-iitm.in/',
    admin_email: 'contactaieciitm@gmail.com',
    whatsapp_link: 'https://chat.whatsapp.com/CqrgZUl7Gj1L6k1pLtF2sW'
  });
};

// ─── Admin Notification ───────────────────────────────────────────────────────
export const sendAdminNotificationEmail = async (applicationData) => {
  return sendEmail('application_confirmation', {
    to_email: 'contactaieciitm@gmail.com',
    to_name: 'AIEC Admin',
    applicant_name: applicationData.fullName,
    applicant_email: applicationData.email,
    department: applicationData.selectedRole,
    course_year: applicationData.courseYear,
    contact_number: applicationData.contactNumber,
    motivation: applicationData.motivation,
    skill_level: applicationData.skillLevel,
    application_date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    application_status: 'New Application Received',
    organization_name: 'AIEC IITM Janakpuri',
    website_url: 'https://aiec-iitm.in/',
    admin_email: 'contactaieciitm@gmail.com'
  });
};

// ─── Ducat Educational Visit ──────────────────────────────────────────────────
export const sendDucatVisitConfirmationEmail = async (registrationData) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(registrationData.email)) return false;

  return sendEmail('ducat_confirmation', {
    to_email: registrationData.email,
    to_name: registrationData.fullName,
    applicant_name: registrationData.fullName,
    applicant_email: registrationData.email,
    course_year: registrationData.branchYear,
    contact_number: registrationData.contactNumber,
    section: registrationData.section,
    enrollment_number: registrationData.enrollmentNo,
    event_name: 'Educational Visit to Ducat School of AI',
    event_location: 'Block A, A4, A-Block, Vikaspuri, New Delhi, Delhi 110018',
    event_dates: 'October 13, 2025',
    event_day: registrationData.selectedDay,
    registration_date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    whatsapp_link: 'https://chat.whatsapp.com/G5naQbfj4sx7JxBqkWqRsS?mode=ems_copy_t',
    calendar_link: 'https://calendar.google.com/calendar/render?action=TEMPLATE&text=Educational%20Visit%20to%20Ducat%20School%20of%20AI&dates=20251013T050000Z/20251013T080000Z',
    organization_name: 'AIEC IITM Janakpuri',
    website_url: 'https://aiec-iitm.in/',
    admin_email: 'contactaieciitm@gmail.com'
  });
};

// ─── IMC 25 Industrial Visit ──────────────────────────────────────────────────
export const sendIndustrialVisitConfirmationEmail = async (registrationData) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(registrationData.email)) return false;

  const selectedDay = registrationData.selectedDay || '';
  const whatsappLinksByDay = {
    'IMC 25 — Day 1': 'https://chat.whatsapp.com/Ja0eaaAkuVg2WdPpoqc3Lw?mode=ems_wa_t',
    'IMC 25 — Day 2': 'https://chat.whatsapp.com/GG6GYFlT5ZAHdSOh6tybeE?mode=ems_wa_t',
    'IMC 25 — Day 3': 'https://chat.whatsapp.com/DTa6x5dVQem6FS4B9qoaOf?mode=ems_wa_t',
    'IMC 2025 – Day 1': 'https://chat.whatsapp.com/Ja0eaaAkuVg2WdPpoqc3Lw?mode=ems_wa_t',
    'IMC 2025 – Day 2': 'https://chat.whatsapp.com/GG6GYFlT5ZAHdSOh6tybeE?mode=ems_wa_t',
    'IMC 2025 – Day 3': 'https://chat.whatsapp.com/DTa6x5dVQem6FS4B9qoaOf?mode=ems_wa_t',
  };
  const eventDatesByDay = {
    'IMC 25 — Day 1': '20251009', 'IMC 25 — Day 2': '20251010', 'IMC 25 — Day 3': '20251011',
    'IMC 2025 – Day 1': '20251009', 'IMC 2025 – Day 2': '20251010', 'IMC 2025 – Day 3': '20251011',
  };

  return sendEmail('imc25_confirmation', {
    to_email: registrationData.email,
    to_name: registrationData.fullName,
    applicant_name: registrationData.fullName,
    applicant_email: registrationData.email,
    event_day: selectedDay,
    course_year: registrationData.branchYear,
    contact_number: registrationData.contactNumber,
    section: registrationData.section,
    enrollment_number: registrationData.enrollmentNo,
    registration_date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    organization_name: 'AIEC IITM Janakpuri',
    website_url: 'https://aiec-iitm.in/',
    admin_email: 'contactaieciitm@gmail.com',
    event_name: 'IMC 25 Industrial Visit',
    event_location: 'Yashobhoomi, New Delhi',
    event_dates: 'October 9-11, 2025',
    ticket_url: 'https://register.indiamobilecongress.com/addAttendees/Student',
    calendar_link: `https://calendar.google.com/calendar/render?action=TEMPLATE&text=IMC%2025%20Industrial%20Visit&location=Yashobhoomi%2C%20New%20Delhi&dates=${eventDatesByDay[selectedDay] || '20251009'}T050000Z%2F${eventDatesByDay[selectedDay] || '20251009'}T090000Z`,
    whatsapp_link: whatsappLinksByDay[selectedDay] || '#',
    qr_ticket_img: registrationData.qrImageDataUrl || ''
  });
};

// ─── Quiz Competition ─────────────────────────────────────────────────────────
export const sendQuizConfirmationEmail = async (registrationData) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(registrationData.email)) return false;

  return sendEmail('quiz_confirmation', {
    to_email: registrationData.email,
    to_name: registrationData.fullName,
    applicant_name: registrationData.fullName,
    applicant_email: registrationData.email,
    course_year: registrationData.branchYear,
    contact_number: registrationData.contactNumber,
    section: registrationData.section,
    enrollment_number: registrationData.enrollmentNo,
    registration_date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    whatsapp_link: registrationData.whatsappLink || 'https://chat.whatsapp.com/YOUR_QUIZ_GROUP_LINK',
    organization_name: 'AIEC IITM Janakpuri',
    website_url: 'https://aiec-iitm.in/',
    admin_email: 'contactaieciitm@gmail.com'
  });
};

// ─── Web Design Competition ───────────────────────────────────────────────────
export const sendWebDesignConfirmationEmail = async (registrationData) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(registrationData.email)) return false;

  return sendEmail('webdesign_confirmation', {
    to_email: registrationData.email,
    to_name: registrationData.fullName,
    applicant_name: registrationData.fullName,
    applicant_email: registrationData.email,
    course_year: registrationData.branchYear,
    contact_number: registrationData.contactNumber,
    section: registrationData.section,
    enrollment_number: registrationData.enrollmentNo,
    registration_date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    whatsapp_link: registrationData.whatsappLink || 'https://chat.whatsapp.com/IFTfPYPS5IlDi6L9fyTjog',
    organization_name: 'AIEC IITM Janakpuri',
    website_url: 'https://aiec-iitm.in/',
    admin_email: 'contactaieciitm@gmail.com'
  });
};

// ─── Dynamic Event Registration ───────────────────────────────────────────────
export const sendEventRegistrationConfirmationEmail = async ({ eventTitle, eventDate, eventLocation, applicantName, applicantEmail, formResponses }) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(applicantEmail)) return false;

  return sendEmail('event_registration_confirmation', {
    to_email: applicantEmail,
    to_name: applicantName,
    applicant_name: applicantName,
    event_title: eventTitle,
    event_date: eventDate || '',
    event_location: eventLocation || '',
    form_responses: formResponses || {},
    registration_date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    organization_name: 'AIEC IITM Janakpuri',
    website_url: 'https://aiec-iitm.in/',
    admin_email: 'contactaieciitm@gmail.com'
  });
};

// ─── Test Configuration ───────────────────────────────────────────────────────
export const testEmailConfiguration = async () => {
  try {
    const response = await fetch(`${SERVER_URL}/api/health`);
    const result = await response.json();
    console.log('Server health:', result);
    return result.status === 'ok';
  } catch (error) {
    console.error('❌ Server health check failed:', error);
    return false;
  }
};
